<script setup>
import { computed } from 'vue';

const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  max: {
    type: Number,
    required: true,
  },
  // For fields that are never HTML (a name, a subject line): parsing would
  // be wasted work at best and, for a value never guaranteed well-formed,
  // risks treating incidental '<'/'>' as tags. Still counted by codepoint,
  // same as the HTML path below — see that comment for why.
  plainText: {
    type: Boolean,
    default: false,
  },
  // Both pge field wrappers reserve a fixed row beneath the control for a hint
  // a counter-only field never renders — 24px on pge-input-text (an 80px
  // container around a 56px field), 12px on pge-textarea. Naming the wrapper
  // here pulls the counter back over that reserved row, leaving only the
  // parent's own gap, so it reads as belonging to the control above it.
  field: {
    type: String,
    default: '',
    validator: (value) => ['', 'input', 'textarea'].includes(value),
  },
});

// Formatting must not consume a recipient's character budget, so the counter
// measures rendered visible text, not markup. A regex can't parse HTML: a
// bare < or > used as punctuation desyncs it from the real tag boundaries,
// under- or over-counting depending on where the next bracket happens to
// fall. DOMParser-produced documents are inert (no scripts run, no
// resources load), so a real parse is both correct and as safe as the
// regex it replaces — the editor upstream already sends well-formed HTML,
// with literal '<'/'>' in user text pre-encoded as entities.
//
// Counted by codepoint via Array.from, not JS's native UTF-16 .length: a
// character outside the Basic Multilingual Plane (most emoji) is one
// codepoint but two UTF-16 units, and the server sizes strings with PHP's
// mb_strlen, which counts codepoints — raw .length would silently disagree
// with the server on any such content.
const visibleLength = computed(() => {
  const text = props.plainText
    ? String(props.value ?? '')
    : new DOMParser().parseFromString(props.value ?? '', 'text/html').body.textContent;
  return Array.from(text).length;
});

// "Running out of room" is a number of characters left, not a fraction of the
// limit: a flat 10% is 200 characters of runway on a 2,000-character body,
// which warns while the message is barely nine-tenths written. Capping the
// runway keeps the warning meaningful on long fields, and the 10% floor keeps
// it from covering most of a short one (a 60-character name warns at 54).
const NEAR_THRESHOLD_CHARS = 20;

const isOver = computed(() => visibleLength.value > props.max);
const isNear = computed(() => {
  const remaining = props.max - visibleLength.value;
  return remaining >= 0 && remaining <= Math.min(NEAR_THRESHOLD_CHARS, Math.ceil(props.max * 0.1));
});

const stateClass = computed(() => {
  if (isOver.value) return 'character-counter--over';
  if (isNear.value) return 'character-counter--near';
  return '';
});

const fieldClass = computed(() => (props.field ? `character-counter--after-${props.field}` : ''));

// A save-gate needs the same over-limit verdict this component already
// renders. Exposing it here keeps that one measurement the only one that
// exists, rather than a consumer re-deriving it (DOMParser + Array.from is
// exactly the logic that shipped a bug twice — see the comments above).
defineExpose({ visibleLength, isOver });
</script>

<template>
  <span
    class="character-counter"
    :class="[stateClass, fieldClass]"
    :aria-invalid="isOver ? 'true' : undefined"
  >{{ visibleLength }} / {{ max }} characters</span>
</template>

<style scoped lang="scss">
.character-counter {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;

  &--near {
    color: #b45309;
  }

  &--over {
    color: #c0392b;
  }

  // See the `field` prop: these cancel the empty hint row each pge field
  // wrapper reserves, so what separates the counter from the control is the
  // parent's own gap rather than that gap plus dead space.
  // Both modifiers used to pull the counter up into the empty error slot the
  // old pge inputs reserved below their control. XlField reserves nothing, so
  // pulling up now lands the counter on the field itself.
  &--after-input,
  &--after-textarea {
    margin-top: 0;
  }
}
</style>
